import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import PlayerSetup from './components/PlayerSetup';
import Tutorial from './components/Tutorial';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import Puzzles from './components/Puzzles';
import PuzzleBoard from './components/PuzzleBoard';
import Matchmaking from './components/Matchmaking';
import OnlineGameBoard from './components/OnlineGameBoard';
import Settings from './components/Settings';
import MyGames from './components/MyGames';
import AnalysisBoard from './components/AnalysisBoard';
import EndgameTrainer from './components/EndgameTrainer';
import Leaderboard from './components/Leaderboard';
import OrderPage from './components/OrderPage';
import HamburgerMenu from './components/HamburgerMenu';
import GameRules from './components/GameRules';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { applyTheme, getSavedTheme } from './utils/themes';

// Apply persisted board theme before any component mounts so the board never
// flashes the default palette mid-render.
applyTheme(getSavedTheme());

// Active-game pointer so a page reload doesn't strand the user. Cleared on explicit
// navigation away (back to menu, save+exit, forfeit). The auto-resume effect verifies
// the game is still active before restoring.
const ACTIVE_GAME_KEY = 'activeGameId';
const setActiveGameId = (id) => { try { localStorage.setItem(ACTIVE_GAME_KEY, String(id)); } catch (_) {} };
const getActiveGameId = () => { try { return localStorage.getItem(ACTIVE_GAME_KEY); } catch (_) { return null; } };
const clearActiveGameId = () => { try { localStorage.removeItem(ACTIVE_GAME_KEY); } catch (_) {} };

function AppContent() {
  const { user, isAuthenticated, logout, loading } = useAuth();
  const [gameMode, setGameMode] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [currentPuzzleId, setCurrentPuzzleId] = useState(null);
  const [currentAnalysisGameId, setCurrentAnalysisGameId] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('landing'); // 'landing', 'login', 'signup', 'forgot-password', 'menu', 'setup', 'game', 'tutorial', 'puzzles', 'puzzle-board', 'matchmaking', 'online-game', 'settings', 'admin'
  const autoResumeAttempted = useRef(false);

  const startGameMode = (mode) => {
    setGameMode(mode);
    setCurrentScreen('setup');
  };

  const handleStartGame = async (opponentName, timeControls, importedGame = null, aiConfig = null) => {
    try {
      // Use the new API
      const { api } = await import('./utils/api');
      const gameInfo = await api.createGame(gameMode, opponentName, aiConfig, timeControls, importedGame);

      setGameData({
        id: gameInfo.id,
        whitePlayerName: gameInfo.whitePlayerName,
        blackPlayerName: gameInfo.blackPlayerName,
        gameMode: gameInfo.gameMode,
        timeControls: timeControls,
        importedGame: importedGame,
        aiConfig: aiConfig
      });
      setActiveGameId(gameInfo.id);
      setCurrentScreen('game');
    } catch (error) {
      console.error('Error creating game:', error);
      alert(`Error creating game: ${error.message}`);
    }
  };

  const backToMenu = () => {
    clearActiveGameId();
    setGameMode(null);
    setGameData(null);
    setCurrentScreen('menu');
  };

  // Auto-resume: on first authenticated load, if the user has a stored active game id
  // and the server still considers it active, restore the gameData and jump straight
  // to the game screen. Runs once per session. The effect is gated on currentScreen
  // being the initial 'landing' so we don't yank a user who's already navigated.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (autoResumeAttempted.current) return;
    if (currentScreen !== 'landing' && currentScreen !== 'menu') return;

    const storedId = getActiveGameId();
    if (!storedId) return;

    autoResumeAttempted.current = true;

    (async () => {
      try {
        const { api } = await import('./utils/api');
        const game = await api.getGame(storedId);
        if (!game || game.status !== 'active') {
          clearActiveGameId();
          return;
        }
        const boardState = await api.getBoardState(storedId);
        if (!boardState || !boardState.board || boardState.gameOver) {
          clearActiveGameId();
          return;
        }
        const inferredBlack = (game.game_mode === 'ai' && !game.black_player_name)
          ? 'Computer AI'
          : game.black_player_name;
        setGameMode(game.game_mode);
        setGameData({
          id: game.id,
          whitePlayerName: game.white_player_name,
          blackPlayerName: inferredBlack,
          gameMode: game.game_mode,
          importedGame: {
            board: boardState.board,
            currentPlayer: boardState.currentPlayer,
          },
        });
        setCurrentScreen('game');
      } catch (e) {
        console.warn('Auto-resume failed, clearing pointer:', e);
        clearActiveGameId();
      }
    })();
  }, [loading, isAuthenticated, currentScreen]);

  const handleLogout = async () => {
    await logout();
    backToMenu();
  };

  const backToSetup = () => {
    setCurrentScreen('setup');
  };

  // Bottom-nav handler — mobile-only nav rolls every tab into one of four
  // home screens. Tapping "Play" while in-game returns to the menu (game
  // state remains on the server; user can resume via My Games).
  const handleHamburgerNavigate = (tab) => {
    if (tab === 'menu') {
      clearActiveGameId();
      setGameMode(null);
      setGameData(null);
      setMatchData(null);
      setCurrentScreen('menu');
    } else if (tab === 'puzzles') {
      setCurrentScreen('puzzles');
    } else if (tab === 'my-games') {
      setCurrentScreen('my-games');
    } else if (tab === 'leaderboard') {
      setCurrentScreen('leaderboard');
    } else if (tab === 'rules') {
      setCurrentScreen('rules');
    } else if (tab === 'settings') {
      setCurrentScreen('settings');
    }
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="App">
        <div className="App-header">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  // Show landing page if not authenticated and on landing screen
  if (!isAuthenticated && currentScreen === 'landing') {
    return (
      <div className="App">
        <LandingPage
          onLogin={() => setCurrentScreen('login')}
          onSignup={() => setCurrentScreen('signup')}
          onOrder={() => setCurrentScreen('order')}
        />
      </div>
    );
  }

  // Show order page (waitlist for physical sets) — accessible without auth
  if (currentScreen === 'order') {
    return (
      <div className="App">
        <OrderPage onBack={() => setCurrentScreen('landing')} />
      </div>
    );
  }

  // Show forgot password screen
  if (currentScreen === 'forgot-password') {
    return (
      <div className="App">
        <ForgotPassword 
          onBackToLogin={() => setCurrentScreen('login')}
        />
      </div>
    );
  }

  // Show login screen
  if (!isAuthenticated && currentScreen === 'login') {
    return (
      <div className="App">
        <Login 
          onSwitchToSignup={() => setCurrentScreen('signup')}
          onSwitchToForgotPassword={() => setCurrentScreen('forgot-password')}
          onSuccess={() => setCurrentScreen('menu')}
        />
      </div>
    );
  }

  // Show signup screen
  if (!isAuthenticated && currentScreen === 'signup') {
    return (
      <div className="App">
        <Signup 
          onSwitchToLogin={() => setCurrentScreen('login')}
          onSuccess={() => setCurrentScreen('menu')}
        />
      </div>
    );
  }

  if (currentScreen === 'menu') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="user-info">
            Welcome, {user?.displayName || user?.username}! 
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
          <h1>Ranga</h1>
          <div className="game-modes">
            <button 
              className="mode-button tutorial-button" 
              onClick={() => setCurrentScreen('tutorial')}
            >
              📚 Learn How to Play
            </button>
            <button 
              className="mode-button" 
              onClick={() => startGameMode('local')}
            >
              Human vs Human Local
            </button>
            <button 
              className="mode-button" 
              onClick={() => startGameMode('ai')}
            >
              Human vs Computer
            </button>
            <button 
              className="mode-button online-button" 
              onClick={() => setCurrentScreen('matchmaking')}
            >
              🌐 Play Online Multiplayer
            </button>
            <button 
              className="mode-button" 
              onClick={() => setCurrentScreen('puzzles')}
            >
              🧩 Solve Tactical Puzzles
            </button>
            <button 
              className="mode-button settings-button" 
              onClick={() => setCurrentScreen('settings')}
            >
              ⚙️ Settings
            </button>
            <button
              className="mode-button my-games-button"
              onClick={() => setCurrentScreen('my-games')}
            >
              📂 My Games
            </button>
          </div>
        </header>
        <HamburgerMenu currentScreen="menu" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'tutorial') {
    return (
      <div className="App">
        <Tutorial onComplete={backToMenu} />
        <HamburgerMenu currentScreen="tutorial" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'setup') {
    return (
      <div className="App">
        <PlayerSetup
          gameMode={gameMode}
          onStartGame={handleStartGame}
          onBack={backToMenu}
        />
        <HamburgerMenu currentScreen="setup" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'game') {
    return (
      <div className="App">
        <GameBoard
          mode={gameMode}
          gameData={gameData}
          onBackToMenu={backToMenu}
          onBackToSetup={backToSetup}
          onSaveAndExit={() => {
            // Game is already saved in database, just exit to menu
            clearActiveGameId();
            setGameData(null);
            setCurrentScreen('menu');
          }}
        />
        <HamburgerMenu currentScreen="game" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'puzzles') {
    return (
      <div className="App">
        <Puzzles
          onBack={backToMenu}
          onStartPuzzle={(puzzleId) => {
            setCurrentPuzzleId(puzzleId);
            setCurrentScreen('puzzle-board');
          }}
          onOpenEndgameTrainer={() => setCurrentScreen('endgame-trainer')}
        />
        <HamburgerMenu currentScreen="puzzles" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'puzzle-board') {
    return (
      <div className="App">
        <PuzzleBoard
          puzzleId={currentPuzzleId}
          onBack={() => setCurrentScreen('puzzles')}
          onComplete={() => setCurrentScreen('puzzles')}
        />
        <HamburgerMenu currentScreen="puzzle-board" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'matchmaking') {
    return (
      <div className="App">
        <Matchmaking
          onMatchFound={(match) => {
            setMatchData(match);
            setCurrentScreen('online-game');
          }}
          onBack={backToMenu}
        />
        <HamburgerMenu currentScreen="matchmaking" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'online-game') {
    return (
      <div className="App">
        <OnlineGameBoard
          matchData={matchData}
          onGameEnd={(result) => {
            setMatchData(null);
            setCurrentScreen('menu');
          }}
        />
        <HamburgerMenu currentScreen="online-game" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'settings') {
    return (
      <div className="App">
        <Settings onBack={backToMenu} />
        <HamburgerMenu currentScreen="settings" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'rules') {
    return (
      <div className="App">
        <GameRules onBack={backToMenu} />
        <HamburgerMenu currentScreen="rules" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  if (currentScreen === 'analysis') {
    return (
      <div className="App">
        <AnalysisBoard
          gameId={currentAnalysisGameId}
          onBack={() => {
            setCurrentAnalysisGameId(null);
            setCurrentScreen('my-games');
          }}
        />
      </div>
    );
  }

  if (currentScreen === 'endgame-trainer') {
    return (
      <div className="App">
        <EndgameTrainer onBack={() => setCurrentScreen('puzzles')} />
      </div>
    );
  }

  if (currentScreen === 'leaderboard') {
    return (
      <div className="App">
        <Leaderboard onBack={backToMenu} currentUserId={user && user.id} />
      </div>
    );
  }

  if (currentScreen === 'my-games') {
    return (
      <div className="App">
        <MyGames
          onBack={backToMenu}
          onAnalyzeGame={(game) => {
            setCurrentAnalysisGameId(game.id);
            setCurrentScreen('analysis');
          }}
          onResumeGame={async (game) => {
            try {
              // Fetch full game details including moves
              const token = localStorage.getItem('authToken');
              const { API_BASE_URL } = await import('./utils/apiBase');
              const response = await fetch(`${API_BASE_URL}/games/${game.id}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (!response.ok) {
                throw new Error('Failed to load game details');
              }
              
              const fullGame = await response.json();
              
              console.log('📥 Full game data:', fullGame);
              
              // Use the latest board_state from the last move instead of replaying
              let board = null;
              let currentPlayer = 'white';
              
              if (fullGame.moves && fullGame.moves.length > 0) {
                // Get the last move which has the current board state
                const lastMove = fullGame.moves[fullGame.moves.length - 1];
                
                console.log('📥 Last move:', lastMove);
                console.log('📥 Last move player_color:', lastMove.player_color);
                
                // Use the board_state from the last move if available
                if (lastMove.board_state && typeof lastMove.board_state === 'string') {
                  try {
                    // board_state is a stringified object: {board: [...], currentPlayer: '...', ...}
                    const parsedState = JSON.parse(lastMove.board_state);
                    
                    // Extract the board array from the state object
                    board = parsedState.board || parsedState;
                    
                    // Use the currentPlayer from the saved state (this is the player who should move next)
                    currentPlayer = parsedState.currentPlayer || (lastMove.player_color === 'white' ? 'black' : 'white');
                    
                    console.log('✅ Parsed board state successfully');
                    console.log('📥 Current player from state:', currentPlayer);
                  } catch (err) {
                    console.error('Failed to parse board_state:', err);
                    // Fallback: determine current player from last move
                    currentPlayer = lastMove.player_color === 'white' ? 'black' : 'white';
                  }
                } else {
                  // Fallback: determine current player from last move
                  currentPlayer = lastMove.player_color === 'white' ? 'black' : 'white';
                  console.log('📥 Calculated current player:', currentPlayer, '(last move was by', lastMove.player_color + ')');
                }
              }
              
              // Set game data with reconstructed board
              // For AI games, ensure blackPlayerName includes "AI"
              const blackPlayerName = fullGame.game_mode === 'ai' && !fullGame.black_player_name 
                ? 'Computer AI' 
                : fullGame.black_player_name;
              
              console.log('📥 Setting game data with names:', {
                whitePlayerName: fullGame.white_player_name,
                blackPlayerName,
                gameMode: fullGame.game_mode
              });
              
              setGameData({
                id: fullGame.id,
                whitePlayerName: fullGame.white_player_name,
                blackPlayerName: blackPlayerName,
                gameMode: fullGame.game_mode,
                importedGame: board ? { 
                  board,
                  currentPlayer,
                  moves: fullGame.moves.map(m => ({
                    from: [m.from_row, m.from_col],
                    to: [m.to_row, m.to_col],
                    player: m.player_color
                  }))
                } : null
              });
              setActiveGameId(fullGame.id);
              setGameMode(fullGame.game_mode);
              setCurrentScreen('game');
            } catch (err) {
              console.error('Error loading game:', err);
              alert('Failed to load game: ' + err.message);
            }
          }}
        />
        <HamburgerMenu currentScreen="my-games" onNavigate={handleHamburgerNavigate} />
      </div>
    );
  }

  // Fallback: If not authenticated and no screen matches, show landing page
  if (!isAuthenticated) {
    return (
      <div className="App">
        <LandingPage
          onLogin={() => setCurrentScreen('login')}
          onSignup={() => setCurrentScreen('signup')}
          onOrder={() => setCurrentScreen('order')}
        />
      </div>
    );
  }

  // Fallback: If authenticated but no screen matches, show menu
  return (
    <div className="App">
      <header className="App-header">
        <div className="user-info">
          Welcome, {user?.displayName || user?.username}! 
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
        <h1>Ranga</h1>
        <div className="game-modes">
          <button className="mode-button" onClick={() => setCurrentScreen('my-games')}>My Games</button>
          <button className="mode-button" onClick={() => startGameMode('ai')}>New Game</button>
        </div>
      </header>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <AppContent />
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
